import * as React from 'react';
import { FC, useCallback } from 'react';

import { VStack } from '@rbilabs/universal-components';
import LottieView from 'lottie-react-native';

import { useCart } from 'hooks/use-cart';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useMinimalCart } from 'hooks/use-minimal-cart';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useGetAvailableRewards } from 'state/loyalty/hooks/use-get-available-rewards';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';
import {
  findRewardBenefitFromHydratedCartEntry,
  mapLegacyToMenuSelections,
} from 'utils/minimal-cart';
import { emptySanityImage } from 'utils/sanity';

import { useRedeemReward } from '../use-redeem-reward-state';

import { ConfettiAnimation } from './confetti-animation';
import { RedeemRewardView } from './redeem-reward.view';
import { IRedeemRewardProps, RewardActions } from './types';

/**
 *
 * RedeemReward card displays below cart items that are eligible for reward redemptions
 * Users can redeem rewards using points from their loyalty balance in order to receive discounts on their order
 *
 */
export const RedeemReward: FC<React.PropsWithChildren<IRedeemRewardProps>> = ({
  isRewardApplied,
  item,
  reward,
}) => {
  const { getAvailableRewardFromCartEntry } = useGetAvailableRewards();
  const { rewardRedeemable, incentiveNotInMenu, redeemRewardLoading } = useRedeemReward(item);
  const dispatch = useAppDispatch();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const { loyaltyUser } = useLoyaltyUser();
  const { pointCost, name } = reward;
  const confettiAnimationRef = React.useRef<LottieView>(null);

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { updateCartEntry } = useCart();
  const { hydrateMinimalCart } = useMinimalCart();

  const handleRewardClick = useCallback(
    (action: RewardActions) => async () => {
      const { applyReward, setIsPricingRewardApplication, unApplyReward } = actions.loyalty;
      dispatch(setIsPricingRewardApplication(true));

      const cartEntryReward = getAvailableRewardFromCartEntry(item);
      if (!cartEntryReward) {
        return;
      }

      if (action === RewardActions.ADD) {
        hapticNotification({ type: HapticsNotificationType.SUCCESS });
        confettiAnimationRef.current?.reset();
        confettiAnimationRef.current?.play();

        if (isUnifiedMenuEnabled) {
          await hydrateMinimalCart([
            {
              id: cartEntryReward.sanityId || cartEntryReward.id,
              rewardBenefitId: cartEntryReward.rewardBenefitId,
              cartId: item.cartId,
              quantity: 1,
              name: item.name,
              // @ts-ignore SanityImage types are a mess
              image: item.image || emptySanityImage(),
              url: item.url,
              children: [],
              isEdit: true,
              menuSelections: mapLegacyToMenuSelections([item]),
            },
          ]);
        }
      }

      if (action === RewardActions.REMOVE) {
        if (isUnifiedMenuEnabled) {
          const rewardBenefit = findRewardBenefitFromHydratedCartEntry(item);
          if (rewardBenefit) {
            updateCartEntry({ ...rewardBenefit, cartId: item.cartId }, item.cartId, true);
          }
        }
      }

      const handleAction = {
        [RewardActions.ADD]: applyReward,
        [RewardActions.REMOVE]: unApplyReward,
      }[action];

      dispatch(
        handleAction({
          cartId: item.cartId,
          rewardBenefitId: cartEntryReward.rewardBenefitId,
          loyaltyUser,
        })
      );
      hapticNotification({ type: HapticsNotificationType.SUCCESS });
    },
    [
      dispatch,
      getAvailableRewardFromCartEntry,
      hydrateMinimalCart,
      isUnifiedMenuEnabled,
      item,
      loyaltyUser,
      updateCartEntry,
    ]
  );

  const numberOfTimesRewardApplied = appliedLoyaltyRewards[item.cartId]?.timesApplied;

  const pointsApplied = (numberOfTimesRewardApplied || 0) * pointCost;

  if (pointCost === 0) {
    return null;
  }

  return (
    <VStack>
      <RedeemRewardView
        handleRewardClick={handleRewardClick}
        isRewardApplied={isRewardApplied}
        points={pointCost}
        rewardTitle={name}
        disableApplyButton={!rewardRedeemable}
        pointsApplied={pointsApplied}
        itemQuantity={item.quantity}
        incentiveNotInMenu={incentiveNotInMenu}
        redeemRewardLoading={redeemRewardLoading}
      />
      <ConfettiAnimation animationRef={confettiAnimationRef} />
    </VStack>
  );
};
